import React, { useEffect, useRef, useState } from "react";
import { Paper } from "@mui/material";
import EnterpriseForm from "./EnterpriseConfig/EnterpriseForm";
import EnterpriseModals from "./EnterpriseConfig/EnterpriseModals";

const EnterpriseConfig = () => {
  const [empresa, setEmpresa] = useState({
    id: null,
    ruc: "",
    razon_social: "",
    nombre_comercial: "",
    numero_establecimiento: "",
    obligado_contabilidad: "NO",
    contribuyente_especial: false,
    gran_contribuyente: false,
    exportador: false,
    exportador_tipo: "NO_HABITUAL",
    contribuyente_rimpe: false,
    rimpe_tipo: "",
    regimen_general: false,
    agente_retencion: false,
    ciudad: "",
    telefonos: "",
    direccion: "",
    numero_decimales: 6,
    email_notificacion: "",
    logo_empresa: null,
  });

  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [preview, setPreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const logoRef = useRef(null);

  const getCSRFToken = () => {
    const name = "csrftoken=";
    const cookies = decodeURIComponent(document.cookie || "").split(";");
    for (let c of cookies) {
      const t = c.trim();
      if (t.indexOf(name) === 0) return t.substring(name.length);
    }
    return "";
  };

  useEffect(() => {
    fetch("/facturas/empresa/obtener/", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.empresa) {
          const e = data.empresa;
          setEmpresa((prev) => ({
            ...prev,
            ...e,
            id: e.id ?? prev.id,
            exportador: !!e.exportador,
            contribuyente_especial: !!e.contribuyente_especial,
            gran_contribuyente: !!e.gran_contribuyente,
            agente_retencion: !!e.agente_retencion,
            contribuyente_rimpe:
              e.regimen_tipo === "EMPRENDEDOR" || e.regimen_tipo === "NEGOCIO_POPULAR"
                ? true
                : !!e.contribuyente_rimpe,
            regimen_general: e.regimen_tipo === "GENERAL",
            rimpe_tipo:
              e.regimen_tipo === "EMPRENDEDOR" || e.regimen_tipo === "NEGOCIO_POPULAR"
                ? e.regimen_tipo
                : "",
            logo_empresa: e.logo_empresa ?? null,
          }));
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "numero_establecimiento") {
      const v = value.replace(/\D/g, "").slice(0, 3);
      setEmpresa((p) => ({ ...p, numero_establecimiento: v }));
      return;
    }
    setEmpresa((p) => ({ ...p, [name]: value }));
  };

  const handleCheck = (e) => {
    const { name, checked } = e.target;
    setEmpresa((p) => {
      const next = { ...p, [name]: checked };
      if (name === "exportador" && !checked) next.exportador_tipo = "NO_HABITUAL";
      if (name === "contribuyente_rimpe") {
        if (checked) next.regimen_general = false;
        if (!checked) next.rimpe_tipo = "";
      }
      if (name === "regimen_general" && checked) {
        next.contribuyente_rimpe = false;
        next.rimpe_tipo = "";
      }
      return next;
    });
  };

  const handleFile = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const ext = file.name.split(".").pop().toLowerCase();
      const allowed = ["jpg", "jpeg", "png"];
      if (!allowed.includes(ext)) {
        setSnackbar({ open: true, severity: "error", message: "Formato no permitido. Solo JPG, JPEG y PNG." });
        e.target.value = "";
        return;
      }
      setEmpresa((p) => ({ ...p, [name]: file }));
    }
  };

  const openPreview = (fileOrUrl) => {
    if (!fileOrUrl) return;
    const url = typeof fileOrUrl === "string" ? fileOrUrl : URL.createObjectURL(fileOrUrl);
    setPreview(url);
    setOpenModal(true);
  };

  const closePreview = () => {
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    setOpenModal(false);
    setPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (empresa.contribuyente_rimpe && !empresa.rimpe_tipo) {
      setSnackbar({ open: true, severity: "error", message: "Seleccione el tipo de RIMPE." });
      return;
    }

    const isEdit = !!empresa.id;
    const url = isEdit ? `/facturas/empresa/editar/${empresa.id}/` : "/facturas/empresa/crear/";
    const form = new FormData();

    form.append("ruc", empresa.ruc || "");
    form.append("razon_social", empresa.razon_social || "");
    form.append("nombre_comercial", empresa.nombre_comercial || "");
    form.append("numero_establecimiento", empresa.numero_establecimiento || "");
    form.append("obligado_contabilidad", empresa.obligado_contabilidad || "NO");
    form.append("contribuyente_especial", String(!!empresa.contribuyente_especial));
    form.append("gran_contribuyente", String(!!empresa.gran_contribuyente));
    form.append("exportador", String(!!empresa.exportador));
    form.append("exportador_tipo", empresa.exportador ? empresa.exportador_tipo || "NO_HABITUAL" : "");
    form.append("agente_retencion", String(!!empresa.agente_retencion));
    form.append("ciudad", empresa.ciudad || "");
    form.append("telefonos", empresa.telefonos || "");
    form.append("direccion", empresa.direccion || "");
    form.append("numero_decimales", String(empresa.numero_decimales ?? 6));
    form.append("email_notificacion", empresa.email_notificacion || "");

    const regimen_tipo = empresa.regimen_general
      ? "GENERAL"
      : empresa.contribuyente_rimpe
      ? empresa.rimpe_tipo || ""
      : "GENERAL";

    form.append("regimen_tipo", regimen_tipo);
    form.append("contribuyente_rimpe", String(!!empresa.contribuyente_rimpe));

    if (empresa.logo_empresa && typeof empresa.logo_empresa !== "string") {
      form.append("logo_empresa", empresa.logo_empresa);
    }

    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRFToken() },
        body: form,
      });
      const data = await res.json();

      if (res.ok) {
        setSnackbar({ open: true, severity: "success", message: "Datos guardados correctamente" });

        if (data.id && !isEdit) setEmpresa((p) => ({ ...p, id: data.id }));
        const r2 = await fetch("/facturas/empresa/obtener/", { credentials: "include" });
        const d2 = await r2.json();
        if (d2?.empresa) {
          setEmpresa((p) => ({
            ...p,
            logo_empresa: d2.empresa.logo_empresa ?? p.logo_empresa,
            contribuyente_rimpe:
              d2.empresa.regimen_tipo === "EMPRENDEDOR" || d2.empresa.regimen_tipo === "NEGOCIO_POPULAR",
            regimen_general: d2.empresa.regimen_tipo === "GENERAL",
            rimpe_tipo:
              d2.empresa.regimen_tipo === "EMPRENDEDOR" || d2.empresa.regimen_tipo === "NEGOCIO_POPULAR"
                ? d2.empresa.regimen_tipo
                : "",
          }));
        }

        if (logoRef.current) logoRef.current.value = "";
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: data?.error ? (typeof data.error === "string" ? data.error : JSON.stringify(data.error)) : "Error al guardar",
        });
      }
    } catch {
      setSnackbar({ open: true, severity: "error", message: "Error de conexión con el servidor" });
    }
  };

  const fileLabel = (f) => {
    if (!f) return "Imagen no seleccionada";
    if (typeof f === "string") return f.split("/").pop();
    return f.name || "Imagen seleccionada";
  };

  return (
    <Paper sx={{ p: 0, borderRadius: "12px", overflow: "hidden", boxShadow: "0 6px 18px rgba(0,0,0,0.1)" }}>
      <EnterpriseForm
        empresa={empresa}
        setEmpresa={setEmpresa}
        handleChange={handleChange}
        handleCheck={handleCheck}
        handleFile={handleFile}
        fileLabel={fileLabel}
        openPreview={openPreview}
        submit={submit}
        logoRef={logoRef}
      />

      <EnterpriseModals
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        openModal={openModal}
        preview={preview}
        closePreview={closePreview}
      />
    </Paper>
  );
};

export default EnterpriseConfig;
